import type { ReactNode } from "react";
import { useEffect } from "react";
import { Languages } from "lucide-react";
import { getSiteCopy } from "../data/i18n";
import { locales } from "../data/locales";
import { getLocalizedTools } from "../data/tools";
import { injectAdSenseScript } from "../lib/adSense";
import { alternatePath, homePath, staticPath } from "../lib/routing";
import type { Locale } from "../types";

type LayoutProps = {
  children: ReactNode;
  locale: Locale;
  pathname: string;
};

export function Layout({ children, locale, pathname }: LayoutProps) {
  const copy = getSiteCopy(locale);
  const tools = getLocalizedTools(locale);

  useEffect(() => {
    injectAdSenseScript();
  }, []);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="site-header">
        <a className="brand" href={homePath(locale)} aria-label="TextPolish Tools home">
          <span className="brand-mark">T</span>
          {copy.brand}
        </a>
        <nav className="site-nav" aria-label={copy.navLabel}>
          <a href={homePath(locale)}>{copy.home}</a>
          {tools.slice(0, 4).map((tool) => (
            <a key={tool.id} href={tool.path}>
              {tool.shortName}
            </a>
          ))}
          <a href={staticPath("extension", locale)}>{copy.extension}</a>
          <a href={staticPath("pricing", locale)}>{copy.pricing}</a>
        </nav>
        <div className="language-switch">
          <Languages size={16} aria-hidden="true" />
          <select
            value={locale}
            aria-label={copy.languageLabel}
            onChange={(event) => {
              window.location.href = alternatePath(pathname, event.target.value as Locale);
            }}
          >
            {locales.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.nativeName}
              </option>
            ))}
          </select>
        </div>
      </header>
      {children}
      <footer className="site-footer">
        <span>{copy.footer}</span>
        <nav aria-label="Footer navigation">
          <a href={staticPath("privacy", locale)}>{copy.privacy}</a>
          <a href={staticPath("terms", locale)}>{copy.terms}</a>
          <a href={staticPath("contact", locale)}>{copy.contact}</a>
          <a href={staticPath("extension", locale)}>{copy.extension}</a>
          <a href={staticPath("pricing", locale)}>{copy.pricing}</a>
        </nav>
      </footer>
    </div>
  );
}
